import { useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import { minutesToSeconds } from 'date-fns';

import { StreamTypes } from 'store/player/actions.types';
import {
    selectStartoverCapability,
    selectTimeshiftCapability,
} from 'store/user/selectors';
import { isCapabilityValid } from 'store/utils';

import parseVideoItemForSO from 'services/data-manager/parsing/parseVideoItemForSO';
import { VideoItem } from 'services/data-manager/types';

import {
    DEFAULT_SO_STREAM_DELAY_FROM_LIVE,
    isStartoverStreamStatic,
} from 'screens/PlayerModal/utils';

import { StartoverStreamType, SwitchPlayerSource } from '../types';

type Params = {
    videoItem: VideoItem | undefined;
    startoverStreamType: StartoverStreamType;
    onSwitchPlayerSource: SwitchPlayerSource;
};

export const TIMESHIFT_BUFFER = minutesToSeconds(240);

function useTimeshift({
    videoItem,
    startoverStreamType,
    onSwitchPlayerSource,
}: Params) {
    const timeshiftCapability = useSelector(selectTimeshiftCapability);
    const startoverCapability = useSelector(selectStartoverCapability);

    const [timeshiftModeOn, setTimeshiftModeOn] = useState(false);
    const [timeshiftPauseByUser, setTimeshiftPauseByUser] = useState(false);
    const [timeshiftPosition, setTimeshiftPosition] = useState<number | null>(
        null,
    );

    const epgExists = !!(videoItem?.startTime && videoItem?.endTime);
    const startoverAllowed = !!(
        isCapabilityValid(startoverCapability) &&
        epgExists &&
        videoItem?.startoverAllowed
    );
    const timeshiftAllowed =
        isCapabilityValid(timeshiftCapability) &&
        !!videoItem?.timeshiftAllowed &&
        epgExists;

    const timeshiftDuration = TIMESHIFT_BUFFER;
    const timeshiftIndicatorEnabled =
        timeshiftModeOn && timeshiftPauseByUser && !startoverAllowed;

    const handlePauseLiveStream = useCallback(
        (playerProgress: number) => {
            let sourceConfig: VideoItem['sourceConfig'];

            if (isStartoverStreamStatic(startoverStreamType)) {
                sourceConfig = {
                    startOffset: playerProgress + (videoItem?.guardTime || 0),
                };
            } else {
                sourceConfig = {
                    startOffset: -DEFAULT_SO_STREAM_DELAY_FROM_LIVE,
                };
            }

            if (videoItem) {
                onSwitchPlayerSource(
                    parseVideoItemForSO(videoItem, sourceConfig),
                    StreamTypes.startover,
                );
            }
        },
        [startoverStreamType, onSwitchPlayerSource, videoItem],
    );

    const handleSwitchTimeshiftModeOn = useCallback(
        (position: number, isUserPaused?: boolean) => {
            setTimeshiftModeOn(true);
            setTimeshiftPosition(
                Math.max(0, position + (videoItem?.guardTime || 0)),
            );
            setTimeshiftPauseByUser(!!isUserPaused);

            handlePauseLiveStream(position);
        },
        [videoItem?.guardTime, handlePauseLiveStream],
    );

    const handleSwitchTimeshiftModeOff = useCallback(() => {
        setTimeshiftModeOn(false);
        setTimeshiftPosition(null);
    }, []);

    return {
        timeshiftAllowed,
        timeshiftModeOn,
        timeshiftPosition,
        timeshiftDuration,
        timeshiftIndicatorEnabled,
        onSwitchTimeshiftModeOn: handleSwitchTimeshiftModeOn,
        onSwitchTimeshiftModeOff: handleSwitchTimeshiftModeOff,
        onPauseLiveStream: handlePauseLiveStream,
    };
}

export default useTimeshift;
